import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useMainButton } from '@/hooks/useMainButton';
import { useHaptics } from '@/hooks/useHaptics';
import { useLinkPlayerMutation } from '@/services/qbotApi';
import type { QbotApiError } from '@/types/qbot';

/**
 * CR tag picker — first step in linking a Clash Royale account to the
 * authenticated Logto user. The form accepts any tag-shaped string;
 * actual validation happens server-side (QBot calls the Supercell API
 * to confirm the player exists before persisting).
 *
 * On success, navigates to /cr (the home) and RTK Query invalidates the
 * Identity tag so the Hub tile flips from "Link" to "Open" without a
 * refresh.
 */

const CR_TAG_RE = /^[#]?[0289PYLQGRJCUV]{3,12}$/i;

function isQbotApiError(err: unknown): err is QbotApiError {
  return typeof err === 'object' && err !== null && 'data' in err;
}

export function CRLink() {
  const { t } = useTranslation('cr');
  const navigate = useNavigate();
  const haptics = useHaptics();
  const [tag, setTag] = useState('');
  const [link, { isLoading }] = useLinkPlayerMutation();
  const [error, setError] = useState<string | null>(null);

  const valid = CR_TAG_RE.test(tag.trim());

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!valid || isLoading) return;
    setError(null);
    try {
      const cleanTag = tag.trim().replace(/^#/, '').toUpperCase();
      await link({ tag: cleanTag }).unwrap();
      haptics.notification('success');
      navigate('/cr', { replace: true });
    } catch (err) {
      haptics.notification('error');
      const code = isQbotApiError(err) ? err.data?.error?.code : undefined;
      setError(
        code === 'NOT_FOUND'
          ? t('link.error.notFound')
          : code === 'INVALID_TAG'
            ? t('link.error.invalidTag')
            : t('link.error.generic'),
      );
    }
  };

  // Telegram MainButton drives the submission so the keyboard's "Done" still
  // works alongside it. Disabled until the input passes basic shape check.
  useMainButton({
    text: t('link.cta'),
    enabled: valid && !isLoading,
    loading: isLoading,
    onClick: () => void submit(),
  });

  return (
    <form onSubmit={submit} className="flex h-full flex-col gap-4 px-4 py-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-fg">{t('link.title')}</h1>
        <p className="text-sm text-fg-muted">{t('link.subtitle')}</p>
      </header>

      <label className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
          {t('link.field.label')}
        </span>
        <input
          autoFocus
          inputMode="text"
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          value={tag}
          onChange={(e) => {
            setError(null);
            setTag(e.target.value);
          }}
          placeholder="#ABC123"
          maxLength={13}
          className="rounded-lg border border-border bg-bg-secondary px-3 py-2 font-mono text-lg uppercase tracking-wider text-fg placeholder:text-fg-muted/60 focus:border-brand focus:outline-none"
        />
        <span className="text-[11px] text-fg-muted">{t('link.field.hint')}</span>
      </label>

      {error && (
        <p className="rounded-md bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{error}</p>
      )}

      {/* Inline submit kept for browser dev mode (no Telegram MainButton). */}
      <button
        type="submit"
        disabled={!valid || isLoading}
        className="mt-auto rounded-lg bg-brand py-3 text-sm font-bold uppercase tracking-wide text-brand-fg transition-opacity disabled:opacity-40 active:opacity-80"
      >
        {isLoading ? t('link.cta.loading') : t('link.cta')}
      </button>
    </form>
  );
}
